const isObject = (val) => val && typeof val === 'object' && !Array.isArray(val);

/** Recursively removes soft-deleted items (`null` marks an item that no longer exists) */
function stripDeleted(value) {
	if (!isObject(value)) return value;

	return Object.fromEntries(
		Object.entries(value)
			.filter(([, val]) => val !== null)
			.map(([key, val]) => [key, stripDeleted(val)])
	);
}

export function mergeInto(source, target) {
	// 1. Handle non-object inputs (return target if defined, else source)
	if (!isObject(target)) {
		return target !== undefined ? target : stripDeleted(source);
	}

	// 2. Initialize the result object as a shallow copy of the source
	// This ensures we keep properties from source that aren't in target
	const result = Array.isArray(source) ? [...source] : stripDeleted({ ...source });

	// 3. Iterate through target keys to apply overrides
	Object.entries(target)
		.filter(([key, value]) => Object.prototype.hasOwnProperty.call(target, key) && value !== undefined)
		.forEach(([key, value]) => {
			const sourceValue = source ? source[key] : undefined;
			const targetValue = value;

			if (sourceValue === null) {
				// Soft-deleted in the source, so it stays deleted no matter what the target overrides
				delete result[key];
			} else if (isObject(targetValue) && isObject(sourceValue)) {
				// If both are objects, merge them recursively into the new object
				result[key] = mergeInto(sourceValue, targetValue);
			} else if (Array.isArray(targetValue)) {
				// If it's an array, create a fresh copy of the target array
				result[key] = [...targetValue];
			} else if (targetValue === null) {
				// If target value is null, delete the key from the result
				delete result[key];
			} else {
				// Primitives pass through; objects with no source counterpart still need their own deletions applied
				result[key] = stripDeleted(targetValue);
			}
		});

	return result;
}
